import { useState, useRef, useCallback, useEffect } from 'react';

import { getBatchResumes } from '../services/api';
import { safeLocalStorageSetItem } from '../../utils/safeLocalStorage';

const FULL_RESUME_CACHE_KEY = 'hh_full_resumes_cache_v2';
const FULL_RESUME_CACHE_ORDER_KEY = 'hh_full_resumes_cache_order_v2';
const FULL_RESUME_CACHE_MAX_ENTRIES = 100;

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function readResumeCache(): Record<string, any> {
  if (typeof window === 'undefined') return {};
  const parsed = safeJsonParse<Record<string, any>>(localStorage.getItem(FULL_RESUME_CACHE_KEY));
  return parsed && typeof parsed === 'object' ? parsed : {};
}

function readResumeCacheOrder(): string[] {
  if (typeof window === 'undefined') return [];
  const parsed = safeJsonParse<string[]>(localStorage.getItem(FULL_RESUME_CACHE_ORDER_KEY));
  return Array.isArray(parsed) ? parsed : [];
}

function writeResumeCache(cache: Record<string, any>, order: string[]) {
  if (typeof window === 'undefined') return;
  safeLocalStorageSetItem(FULL_RESUME_CACHE_KEY, JSON.stringify(cache));
  safeLocalStorageSetItem(FULL_RESUME_CACHE_ORDER_KEY, JSON.stringify(order));
}

function touchOrder(order: string[], id: string) {
  const next = order.filter((x) => x !== id);
  next.push(id);
  return next;
}

type CacheMode = 'full' | 'min';

interface ResumeData {
  experience?: Array<{
    position?: string;
    company?: string;
    start?: string;
    end?: string;
    description?: string;
  }>;
  contact?: any[];
  job_search_status?: any;
  age?: number;
  _cacheMode?: CacheMode;
  _minimized?: boolean;
  [key: string]: any;
}

interface UseBackgroundResumeLoaderOptions {
  responses?: any[];
  autoLoad?: boolean;
}

interface UseBackgroundResumeLoaderResult {
  fullResumes: Record<string, ResumeData>;
  loadingResumeIds: Set<string>;
  isLoading: boolean;
  loadSingleResume: (resumeId: string) => Promise<void>;
  failedResumeIds: Set<string>;
  viewLimitReached: boolean;
}

function toErrorMessage(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message || '';
  const anyVal = value as any;
  return (
    anyVal?.response?.data?.details ||
    anyVal?.response?.data?.error ||
    anyVal?.message ||
    ''
  );
}

function isHhResumesViewLimitReached(message: string): boolean {
  const m = String(message || '').toLowerCase();
  return (
    m.includes('resumes view limit') ||
    m.includes('view limit reached') ||
    (m.includes('limit') && m.includes('resume') && m.includes('view')) ||
    (m.includes('лимит') && (m.includes('просмотр') || m.includes('просмотра')))
  );
}

export function useBackgroundResumeLoader(
  options: UseBackgroundResumeLoaderOptions = {}
): UseBackgroundResumeLoaderResult {
  const { responses = [], autoLoad = false } = options;
  
  const [fullResumes, setFullResumes] = useState<Record<string, ResumeData>>({});
  const [loadingResumeIds, setLoadingResumeIds] = useState<Set<string>>(new Set());
  const [failedResumeIds, setFailedResumeIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [viewLimitReached, setViewLimitReached] = useState(false);
  const loadedIdsRef = useRef<Set<string>>(new Set());
  const fullResumesRef = useRef<Record<string, ResumeData>>({});
  const viewLimitReachedRef = useRef(false);

  useEffect(() => {
    fullResumesRef.current = fullResumes;
  }, [fullResumes]);

  useEffect(() => {
    viewLimitReachedRef.current = viewLimitReached;
  }, [viewLimitReached]);

  const isFullyLoaded = useCallback((resumeId: string) => {
    const data = fullResumesRef.current[resumeId];
    return !!data && data._cacheMode === 'full';
  }, []);

  const getCacheEntry = useCallback((resumeId: string) => {
    const cache = readResumeCache();
    const raw = cache[resumeId];
    if (!raw || typeof raw !== 'object') return null;
    const cacheMode: CacheMode =
      raw._cacheMode === 'full' || raw._cacheMode === 'min'
        ? raw._cacheMode
        : 'min';
    return { data: { ...raw, _cacheMode: cacheMode } as ResumeData, cacheMode };
  }, []);

  const hydrateFromStorage = useCallback((resumeIds: string[]) => {
    const uniqueIds = Array.from(new Set(resumeIds.filter((id): id is string => !!id)));
    if (uniqueIds.length === 0) return { found: 0 };

    const cache = readResumeCache();
    const foundItems: Record<string, ResumeData> = {};
    let found = 0;

    for (const id of uniqueIds) {
      const data = cache[id];
      if (data) {
        const cacheMode: CacheMode =
          data?._cacheMode === 'full' || data?._cacheMode === 'min'
            ? data._cacheMode
            : 'min';
        foundItems[id] = { ...(data as any), _cacheMode: cacheMode };
        found++;
      }
    }

    if (found > 0) {
      setFullResumes((prev) => ({ ...prev, ...foundItems }));
    }

    return { found };
  }, []);

  const persistToStorage = useCallback((items: Record<string, ResumeData>) => {
    if (typeof window === 'undefined') return;
    const ids = Object.keys(items);
    if (ids.length === 0) return;

    try {
      const cache = readResumeCache();
      let order = readResumeCacheOrder();

      for (const id of ids) {
        const data = items[id];
        if (!data || typeof data !== 'object') continue;
        cache[id] = { ...(data as any), _cacheMode: 'full' as CacheMode };
        order = touchOrder(order, id);
      }

      if (order.length > FULL_RESUME_CACHE_MAX_ENTRIES) {
        const toEvict = order.slice(0, order.length - FULL_RESUME_CACHE_MAX_ENTRIES);
        for (const id of toEvict) {
          delete cache[id];
        }
        order = order.slice(order.length - FULL_RESUME_CACHE_MAX_ENTRIES);
      }

      writeResumeCache(cache, order);
    } catch (e) {
      try {
        const cache = readResumeCache();
        let order = readResumeCacheOrder();
        const keep = order.slice(Math.max(0, order.length - 50));
        const trimmed: Record<string, any> = {};
        for (const id of keep) {
          if (cache[id]) trimmed[id] = cache[id];
        }
        writeResumeCache(trimmed, keep);
      } catch {}
    }
  }, []);

  const loadSingleResume = useCallback(async (resumeId: string) => {
    if (viewLimitReachedRef.current) return;
    if (isFullyLoaded(resumeId) || loadingResumeIds.has(resumeId)) {
      return;
    }

    const cached = getCacheEntry(resumeId);
    if (cached?.data) {
      setFullResumes((prev) => ({ ...prev, [resumeId]: cached.data }));
      if (cached.cacheMode === 'full') return;
    }

    console.log('[responses] full resume request (single)', {
      requested: 1,
      resumeId
    });

    setLoadingResumeIds(prev => new Set([...Array.from(prev), resumeId]));
    setFailedResumeIds(prev => {
      const updated = new Set(prev);
      updated.delete(resumeId);
      return updated;
    });

    try {
      const result = await getBatchResumes([resumeId]);
      
      if (result.resumes && result.resumes.length > 0) {
        const item = result.resumes[0];
        if (item.data) {
          setFullResumes(prev => ({ ...prev, [resumeId]: { ...(item.data as any), _cacheMode: 'full' as CacheMode } }));
          loadedIdsRef.current.add(resumeId);
          persistToStorage({ [resumeId]: item.data });
        } else if (item.error) {
          const msg = String(item.error || '');
          if (isHhResumesViewLimitReached(msg)) {
            setViewLimitReached(true);
          }
          setFailedResumeIds(prev => new Set([...Array.from(prev), resumeId]));
        }
      }
    } catch (error) {
      console.error('Error loading resume:', error);
      const msg = toErrorMessage(error);
      if (isHhResumesViewLimitReached(msg)) {
        setViewLimitReached(true);
      }
      setFailedResumeIds(prev => new Set([...Array.from(prev), resumeId]));
    } finally {
      setLoadingResumeIds(prev => {
        const updated = new Set(prev);
        updated.delete(resumeId);
        return updated;
      });
    }
  }, [loadingResumeIds, persistToStorage, isFullyLoaded, getCacheEntry]);

  const loadBatch = useCallback(async (resumeIds: string[]) => {
    if (viewLimitReachedRef.current) return;
    if (resumeIds.length === 0) return;

    const uniqueIds = Array.from(new Set(resumeIds.filter((id): id is string => !!id)));
    const cache = readResumeCache();
    const fromCache: Record<string, ResumeData> = {};
    let hydratedCount = 0;

    for (const id of uniqueIds) {
      const raw = cache[id];
      if (!raw || typeof raw !== 'object') continue;
      const cacheMode: CacheMode =
        raw._cacheMode === 'full' || raw._cacheMode === 'min'
          ? raw._cacheMode
          : 'min';
      fromCache[id] = { ...raw, _cacheMode: cacheMode };
      hydratedCount++;
    }

    if (hydratedCount > 0) {
      setFullResumes((prev) => ({ ...prev, ...fromCache }));
    }

    const cachedCount = uniqueIds.filter(id => loadedIdsRef.current.has(id)).length;
    const inFlightCount = uniqueIds.filter(id => loadingResumeIds.has(id)).length;
    const newIds = uniqueIds.filter(
      id =>
        fromCache[id]?._cacheMode !== 'full' &&
        !loadingResumeIds.has(id) &&
        !failedResumeIds.has(id)
    );
    if (newIds.length === 0) return;

    const BATCH_SIZE = 10;
    console.log('[responses] full resumes request (batch)', {
      totalOnPage: responses.length,
      incoming: resumeIds.length,
      unique: uniqueIds.length,
      fromStorage: hydratedCount,
      cached: cachedCount,
      inFlight: inFlightCount,
      requested: newIds.length,
      batches: Math.ceil(newIds.length / BATCH_SIZE),
    });

    setIsLoading(true);
    setLoadingResumeIds(prev => new Set([...Array.from(prev), ...newIds]));

    let shouldStop = false;

    for (let i = 0; i < newIds.length; i += BATCH_SIZE) {
      const batch = newIds.slice(i, i + BATCH_SIZE);
      
      try {
        const result = await getBatchResumes(batch);
        
        if (result.resumes) {
          const newResumes: Record<string, ResumeData> = {};
          
          for (const item of result.resumes) {
            if (item.data) {
              newResumes[item.id] = { ...(item.data as any), _cacheMode: 'full' as CacheMode };
              loadedIdsRef.current.add(item.id);
            } else if (item.error) {
              const msg = String(item.error || '');
              if (isHhResumesViewLimitReached(msg)) {
                setViewLimitReached(true);
                shouldStop = true;
              }
              setFailedResumeIds(prev => new Set([...Array.from(prev), item.id]));
            }
          }
          
          setFullResumes(prev => ({ ...prev, ...newResumes }));
          persistToStorage(newResumes);
        }
      } catch (error) {
        console.error('Error loading batch resumes:', error);
        const msg = toErrorMessage(error);
        if (isHhResumesViewLimitReached(msg)) {
          setViewLimitReached(true);
          shouldStop = true;
        }
        batch.forEach(id => {
          setFailedResumeIds(prev => new Set([...Array.from(prev), id]));
        });
      }

      setLoadingResumeIds(prev => {
        const updated = new Set(prev);
        batch.forEach(id => updated.delete(id));
        return updated;
      });

      if (shouldStop) break;
      if (i + BATCH_SIZE < newIds.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    setIsLoading(false);
    if (shouldStop) {
      setFailedResumeIds(prev => new Set([...Array.from(prev), ...newIds]));
    }
    setLoadingResumeIds(prev => {
      const updated = new Set(prev);
      newIds.forEach(id => updated.delete(id));
      return updated;
    });
  }, [loadingResumeIds, failedResumeIds, persistToStorage, responses.length]);

  useEffect(() => {
    if (!autoLoad || responses.length === 0) return;
    if (viewLimitReached) return;

    const resumeIds = responses
      .map(r => r.resume?.id)
      .filter((id): id is string => !!id && !isFullyLoaded(id) && !failedResumeIds.has(id));

    if (resumeIds.length > 0) {
      loadBatch(resumeIds);
    }
  }, [responses, autoLoad, loadBatch, isFullyLoaded, viewLimitReached, failedResumeIds]);

  return { fullResumes, loadingResumeIds, isLoading, loadSingleResume, failedResumeIds, viewLimitReached };
}
